// frontend/src/pages/Home.jsx
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import ProductCard from "../components/ProductCard";
import BannerSlider from "../components/BannerSlider";
import FeaturedSlider from "../components/FeaturedSlider";
import postService from "../services/postService";
import { Package, Gift, Sparkles, X } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function Home({
  searchTerm = "",
  setSearchTerm,
  selectedCategory = "",
  setSelectedCategory,
  selectedCondominio = "todos",
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTipo, setFilterTipo] = useState("todos");

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const filters = {
      category: selectedCategory,
      condominio: selectedCondominio === "todos" ? "" : selectedCondominio,
      tipo: filterTipo === "todos" ? "" : filterTipo,
    };

    const result = await postService.getPosts(filters);

    if (result.success) {
      let filteredPosts = result.data;

      if (searchTerm && searchTerm.trim() !== "") {
        filteredPosts = filteredPosts.filter(
          (post) =>
            post.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setPosts(filteredPosts);
    } else {
      setError(result.error);
      setPosts([]);
    }

    setLoading(false);
  }, [selectedCategory, selectedCondominio, searchTerm, filterTipo]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // 🔥 Función para obtener el label del filtro
  const getFilterLabel = () => {
    if (filterTipo === "Venda") return "Desapego";
    if (filterTipo === "Doação") return "Doação";
    if (filterTipo === "Novos") return "Novos";
    return "Todos";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 -mt-20 pb-24">
        <div className="mb-8">
          <BannerSlider />
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-[#449879] rounded-full"></div>
              <h2 className="text-xl font-bold text-gray-800"> Publicidade</h2>
            </div>
            <span className="text-xs bg-[#449879] text-white px-2 py-0.5 rounded-full">
              Publicidade
            </span>
          </div>
          <FeaturedSlider />
        </div>

        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <img
                src="/brecho.png"
                alt="Brechó"
                className="w-8 h-8 object-contain"
              />
              <h2 className="text-xl font-bold text-gray-800">Publicações</h2>
            </div>
            
            {/* 🔥 QUICK LINKS - CON "NOVOS" */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setFilterTipo("todos")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  filterTipo === "todos"
                    ? "bg-[#449879] text-white shadow-md shadow-[#449879]/20"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setFilterTipo("Venda")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  filterTipo === "Venda"
                    ? "bg-green-500 text-white shadow-md shadow-green-500/20"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Package className="w-3.5 h-3.5" />
                Desapego
              </button>
              <button
                onClick={() => setFilterTipo("Doação")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  filterTipo === "Doação"
                    ? "bg-blue-500 text-white shadow-md shadow-blue-500/20"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Gift className="w-3.5 h-3.5" />
                Doação
              </button>
              {/* 🔥 NUEVO FILTRO "NOVOS" */}
              <button
                onClick={() => setFilterTipo("Novos")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  filterTipo === "Novos"
                    ? "bg-[#449879] text-white shadow-md shadow-[#449879]/20"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Novos
              </button>
              {filterTipo !== "todos" && (
                <button
                  onClick={() => setFilterTipo("todos")}
                  className="px-2 py-1.5 rounded-full text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-0.5"
                >
                  <X className="w-3 h-3" />
                  Limpar
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#449879]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-red-500 text-lg">{error}</p>
              <button
                onClick={loadPosts}
                className="mt-4 bg-[#449879] text-white px-6 py-2 rounded-lg hover:bg-[#367d64] transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {posts.map((post) => (
                <ProductCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500 text-lg">Nenhum produto encontrado</p>
              <p className="text-gray-400 text-sm mt-2">
                {filterTipo !== "todos" 
                  ? `Nenhum item encontrado em "${getFilterLabel()}"`
                  : "Tente ajustar os filtros ou volte mais tarde"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}